import { GIB } from './capacityEnvelope'
import { BLOCK_SECONDS, parseChainAmount } from './pricing'

/**
 * A provider's published price list, and the price it produces.
 *
 * This mirrors `provider/pricelist` (`PriceList.PerBlock`), the function the
 * provider's quote signer and bid engine both call. The provider publishes its
 * prices per unit per HOUR (`hourly_prices` in the capability document, schema
 * 3; the catalog carries them as `unit_prices`), because that is how an
 * operator writes them: $2 per core-hour has no whole per-block value
 * (3333.33 uddt). A machine's price is therefore:
 *
 *   1. the exact hourly sum — CPU per core × millicores / 1000, memory and
 *      storage per GiB rounded UP to whole GiB, GPUs per unit;
 *   2. divided by the blocks in an hour, and rounded UP once, to whole uddt
 *      per block. That integer is the lease rate the chain charges.
 *
 * The calculation is deterministic by design (ADR D4, Gate 2), so a figure
 * computed here from the same resources is the figure the quote will carry —
 * unless the operator changes the price list in between, which is why the
 * quote stays the binding price and the copy says so.
 *
 * All arithmetic is `bigint`. Hourly amounts are uddt, which is microdollars.
 */
export type UnitRates = {
  cpuPerCoreHour: bigint
  memoryPerGiBHour: bigint
  storagePerGiBHour: bigint
  /** Null when the provider publishes no GPU rate; a request with no GPU does not need one. */
  gpuPerUnitHour: bigint | null
  /** The provider's block cadence; the hourly sum is divided by 3600 / this. */
  blockSeconds: number
}

/** The catalog's `unit_prices` shape, structurally, so `core` stays free of wire types. Amounts are uddt per unit per hour. */
export type UnitPricesLike = { denom: string; block_seconds?: number | null; cpu: string | null; memory: string | null; storage: string | null; gpu: string | null }

/** Capacity as the provider meters it: millicores, bytes, whole GPUs. */
export type MeteredCapacity = { cpuMillicores: bigint; memoryBytes: bigint; storageBytes: bigint; gpuUnits: bigint }

export type PriceAxis = 'cpu' | 'memory' | 'storage' | 'gpu'
export type PriceLine = {
  axis: PriceAxis
  /** In the unit a tenant reads: cores, GB (GiB, as everywhere on this page), GPUs. */
  quantity: bigint
  quantityMilli?: bigint
  /** uddt per hour, per displayed unit (per core for CPU). */
  unitPerHour: bigint
  /** uddt per hour for the line, rounded up to a whole uddt (only a fractional CPU amount can need it). */
  totalPerHour: bigint
}
export type CapacityPrice = {
  lines: PriceLine[]
  /** The lease rate: whole uddt per block, the exact hourly sum divided by the blocks in an hour and rounded up once. */
  totalPerBlock: bigint
  /** What that rate charges per hour: `totalPerBlock` × blocks per hour. At most one uddt per block above the lines' sum. */
  totalPerHour: bigint
}

const TENANT_DENOM = 'uddt'
const MILLICORES_PER_CORE = 1000n
const GIB_BIG = BigInt(GIB)
const SECONDS_PER_HOUR = 3600

/** Blocks in an hour, or null for a cadence that does not divide an hour exactly. */
export function blocksPerHour(blockSeconds: number): bigint | null {
  if (!Number.isInteger(blockSeconds) || blockSeconds <= 0 || SECONDS_PER_HOUR % blockSeconds !== 0) return null
  return BigInt(SECONDS_PER_HOUR / blockSeconds)
}

/** Null unless CPU, memory and storage are all readable exact amounts in the tenant denomination, at a usable cadence. */
export function unitRates(prices: UnitPricesLike | null | undefined): UnitRates | null {
  if (!prices || prices.denom !== TENANT_DENOM) return null
  const blockSeconds = prices.block_seconds ?? BLOCK_SECONDS
  if (blocksPerHour(blockSeconds) === null) return null
  const cpu = parseChainAmount(prices.cpu)
  const memory = parseChainAmount(prices.memory)
  const storage = parseChainAmount(prices.storage)
  if (cpu === null || memory === null || storage === null) return null
  return { cpuPerCoreHour: cpu, memoryPerGiBHour: memory, storagePerGiBHour: storage, gpuPerUnitHour: parseChainAmount(prices.gpu), blockSeconds }
}

/** Bytes to whole GiB, rounded up — the provider's `GiBCeil`. */
export function gibCeil(bytes: bigint): bigint {
  return bytes <= 0n ? 0n : (bytes + GIB_BIG - 1n) / GIB_BIG
}

function ceilDiv(numerator: bigint, denominator: bigint): bigint {
  return (numerator + denominator - 1n) / denominator
}

/**
 * The exact hourly price of a capacity, in thousandths of a uddt (CPU is
 * priced per core and metered per millicore, so the exact sum is a whole number
 * only in these units).
 */
function hourlyMilli(rates: UnitRates, capacity: MeteredCapacity): bigint {
  return rates.cpuPerCoreHour * capacity.cpuMillicores
    + (rates.memoryPerGiBHour * gibCeil(capacity.memoryBytes)
      + rates.storagePerGiBHour * gibCeil(capacity.storageBytes)
      + (rates.gpuPerUnitHour ?? 0n) * capacity.gpuUnits) * MILLICORES_PER_CORE
}

/** Whole uddt per block for an exact hourly amount in thousandths of a uddt, rounded up once. */
function milliHourlyToPerBlock(milli: bigint, blockSeconds: number): bigint {
  return ceilDiv(milli, MILLICORES_PER_CORE * (blocksPerHour(blockSeconds) ?? 1n))
}

/**
 * The provider's `PriceList.PerBlock`, with the working shown. Null when the
 * capacity needs a GPU the price list has no price for: a missing rate is not
 * a free GPU.
 */
export function priceForCapacity(rates: UnitRates, capacity: MeteredCapacity): CapacityPrice | null {
  if (capacity.gpuUnits > 0n && rates.gpuPerUnitHour === null) return null
  const memoryGiB = gibCeil(capacity.memoryBytes)
  const storageGiB = gibCeil(capacity.storageBytes)
  const lines: PriceLine[] = [
    {
      axis: 'cpu',
      quantity: capacity.cpuMillicores / MILLICORES_PER_CORE,
      quantityMilli: capacity.cpuMillicores,
      unitPerHour: rates.cpuPerCoreHour,
      totalPerHour: ceilDiv(rates.cpuPerCoreHour * capacity.cpuMillicores, MILLICORES_PER_CORE),
    },
    { axis: 'memory', quantity: memoryGiB, unitPerHour: rates.memoryPerGiBHour, totalPerHour: rates.memoryPerGiBHour * memoryGiB },
    { axis: 'storage', quantity: storageGiB, unitPerHour: rates.storagePerGiBHour, totalPerHour: rates.storagePerGiBHour * storageGiB },
  ]
  if (capacity.gpuUnits > 0n && rates.gpuPerUnitHour !== null) {
    lines.push({ axis: 'gpu', quantity: capacity.gpuUnits, unitPerHour: rates.gpuPerUnitHour, totalPerHour: rates.gpuPerUnitHour * capacity.gpuUnits })
  }
  const totalPerBlock = milliHourlyToPerBlock(hourlyMilli(rates, capacity), rates.blockSeconds)
  return { lines, totalPerBlock, totalPerHour: totalPerBlock * (blocksPerHour(rates.blockSeconds) ?? 1n) }
}

/**
 * What a custom-capacity quote will charge for this draft: exactly the
 * resources asked for, times the replica count (the provider sums the group's
 * resources × count before pricing), with a persistent volume counted as
 * storage. The guided draft has no GPU.
 */
export function priceForDraft(rates: UnitRates, draft: { cpu: number; ram: number; storage: number; volume?: { sizeGi: number } | undefined }, count: number): CapacityPrice | null {
  const n = BigInt(Math.max(1, Math.trunc(count)))
  // A persistent volume is storage the provider prices too: a direct quote
  // sums every volume at the storage rate (`capacityForGroupSpec`), so it is
  // on the price list's storage line.
  const storageGiB = Math.max(0, Math.trunc(draft.storage)) + Math.max(0, Math.trunc(draft.volume?.sizeGi ?? 0))
  return priceForCapacity(rates, {
    cpuMillicores: BigInt(Math.max(0, Math.trunc(draft.cpu))) * n,
    memoryBytes: BigInt(Math.max(0, Math.trunc(draft.ram))) * GIB_BIG * n,
    storageBytes: BigInt(storageGiB) * GIB_BIG * n,
    gpuUnits: 0n,
  })
}

/** A host offer's capacity strings, structurally. */
export type OfferCapacityLike = { cpu_millicores?: string | null; memory_bytes?: string | null; storage_bytes?: string | null; gpu_units?: string | null }

function wholeNumber(value: string | null | undefined): bigint | null {
  return typeof value === 'string' && /^\d+$/.test(value) ? BigInt(value) : null
}

export function meteredCapacity(capacity: OfferCapacityLike | null | undefined): MeteredCapacity | null {
  if (!capacity) return null
  const cpu = wholeNumber(capacity.cpu_millicores)
  const memory = wholeNumber(capacity.memory_bytes)
  const storage = wholeNumber(capacity.storage_bytes)
  if (cpu === null || memory === null || storage === null) return null
  return { cpuMillicores: cpu, memoryBytes: memory, storageBytes: storage, gpuUnits: wholeNumber(capacity.gpu_units) ?? 0n }
}

export type HostProductBreakdown = CapacityPrice & {
  /** Exclusive only: what the posted price adds over the resources, per block. */
  premiumPerBlock: bigint | null
}

/**
 * The working behind a host product's posted rate, or null when it cannot be
 * shown honestly.
 *
 * The provider prices a host product on the host, not on the request
 * (`RateCard.PriceForProduct`): the shared product on everything free now,
 * the exclusive one on the whole machine plus a premium. The offer carries both
 * capacities and the posted price computed from them, so the resource lines
 * must reproduce the posted price exactly (shared). For exclusive the rest is
 * the premium, whose rate is not published but whose form is: the provider
 * multiplies the exact hourly price by `(10000 + bp) / 10000` for an integer
 * `bp` of at most 100000 (`PriceList.Validate`) and rounds up once. A posted
 * price no such `bp` produces means the catalog's prices are not the ones the
 * host priced with — a price list changed since the catalog read, say — and no
 * breakdown is shown. This is a strong filter on realistic bases and a weaker
 * one on tiny ones, so the claim is "consistent with the host's prices", not
 * proof.
 */
export function hostProductBreakdown(
  rates: UnitRates,
  exclusive: boolean,
  offer: { allocatable?: OfferCapacityLike | null; available?: OfferCapacityLike | null },
  postedPerBlock: string,
): HostProductBreakdown | null {
  const posted = parseChainAmount(postedPerBlock)
  const capacity = meteredCapacity(exclusive ? offer.allocatable : offer.available)
  if (posted === null || !capacity) return null
  const base = priceForCapacity(rates, capacity)
  if (!base) return null
  if (!exclusive) return base.totalPerBlock === posted ? { ...base, premiumPerBlock: null } : null
  if (posted < base.totalPerBlock) return null
  const milli = hourlyMilli(rates, capacity)
  if (posted > base.totalPerBlock && !premiumFromBasisPoints(rates, milli, posted) && !legacyPremium(rates, milli, posted)) return null
  return { ...base, premiumPerBlock: posted - base.totalPerBlock }
}

/** The provider's exclusive-premium bound: at most 10x (`PriceList.Validate`). */
const MAX_PREMIUM_BASIS_POINTS = 100_000n

/**
 * Whether some integer basis-point premium turns the exact hourly `milli`
 * price into exactly `posted` per block under the provider's
 * `ceil(milli × (10000 + bp) / 10000 / 1000 / blocksPerHour)`. That is
 * monotonic in bp, so the smallest bp that reaches `posted` is the only
 * candidate worth checking.
 */
function premiumFromBasisPoints(rates: UnitRates, milli: bigint, posted: bigint): boolean {
  if (milli === 0n) return false
  const denominator = 10_000n * MILLICORES_PER_CORE * (blocksPerHour(rates.blockSeconds) ?? 1n)
  const priced = (bp: bigint) => ceilDiv(milli * (10_000n + bp), denominator)
  // ceil(x) >= posted  <=>  x > posted - 1  <=>  milli × (10000 + bp) > (posted - 1) × denominator
  const bp = (posted - 1n) * denominator / milli + 1n - 10_000n
  const candidate = bp < 0n ? 0n : bp
  return candidate <= MAX_PREMIUM_BASIS_POINTS && priced(candidate) === posted
}

/**
 * A provider still on capability schema 1-2 (before the price list) priced the
 * exclusive premium on the rounded per-block base, truncating:
 * `base + floor(base × bp / 10000)`. That only differs from the rule above when
 * `base × bp` is not a multiple of 10000 — for a 20% premium, four bases in
 * five — so without this the breakdown vanished for most not-yet-upgraded
 * hosts during a rollout. Only applies when the hourly price is a whole number
 * of uddt per block, which a converted per-block card always is.
 */
function legacyPremium(rates: UnitRates, milli: bigint, posted: bigint): boolean {
  const perBlockMilli = MILLICORES_PER_CORE * (blocksPerHour(rates.blockSeconds) ?? 1n)
  if (milli === 0n || milli % perBlockMilli !== 0n) return false
  const base = milli / perBlockMilli
  const premium = posted - base
  const bp = (premium * 10_000n + base - 1n) / base
  return bp <= MAX_PREMIUM_BASIS_POINTS && (base * bp) / 10_000n === premium
}
